import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";

import { getRestaurantDetails } from "../actions/restaurantActions";
import { ensureReadableTextColor } from "../utils/contrastColor";
import { t } from "../i18n/translations";
import LanguageSwitcher from "./LanguageSwitcher";
import Seo from "./Seo";
import "./RestaurantHero.css";

const DEFAULT_HEADER_BG = "#ffffff";
const DEFAULT_HEADER_TEXT = "#ffffff";

// JS Date.getDay() is 0=Sunday..6=Saturday; opening_hours uses
// 0=Monday..6=Sunday (matching Python's datetime.weekday(), which the
// backend's is_open_now computation is built on) — convert once here.
const todayWeekday = () => (new Date().getDay() + 6) % 7;

/* Navbar-free identity section for the customer-facing menu (replaces the
   old single-row Header for restaurant subdomains — Header.js now only
   renders on the landing page). A banner (optional — falls back to
   header_bg_color when the owner hasn't uploaded one) with a large,
   non-cropped logo overlapping its bottom edge, name, optional
   description and opening-status pill.

   Scrolls away with the page like any other content — it does not stick.
   Only ProductScreen.js's category bar goes sticky once scrolled past
   (via a sentinel it renders as its own first element), same as before
   this hero existed. */
function RestaurantHero() {
  const dispatch = useDispatch();
  const { pathname, state } = useLocation();
  const { restaurant = {} } = useSelector((state) => state.restaurantDetails || {});
  const { language } = useSelector((state) => state.language);

  /* Opening a product in-app doesn't actually navigate away from the menu
     list (see App.js's AppRoutes — it renders as a sheet over the still-
     mounted background instead), so this hero should keep judging "am I
     behind the product sheet" by the *background* location, not the raw
     URL. Only a direct/shared link to /products/:id (no background state,
     see ProductDetailScreen's own isOverlay check) falls back to its full
     page layout — there, RestaurantHero still trims down since the hero
     genuinely is sitting above the product now. */
  const effectivePathname = state?.backgroundLocation?.pathname ?? pathname;
  const isCompact = effectivePathname.startsWith("/products/");

  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    dispatch(getRestaurantDetails());
  }, [dispatch]);

  useEffect(() => {
    setLogoFailed(false);
  }, [restaurant.logo_image]);

  const bgColor = restaurant.header_bg_color || DEFAULT_HEADER_BG;
  const textColor = ensureReadableTextColor(
    bgColor,
    restaurant.header_text_color || DEFAULT_HEADER_TEXT,
  );

  const hasLogo = restaurant.logo_image && !logoFailed;
  const showName = restaurant.name && restaurant.show_restaurant_name !== false;
  const isOpenNow = restaurant.is_open_now;
  const hasStatus = isOpenNow !== null && isOpenNow !== undefined;

  const openingHours = Array.isArray(restaurant.opening_hours)
    ? [...restaurant.opening_hours].sort((a, b) => a.weekday - b.weekday)
    : [];
  const weekdayLabels = t("weekdays", language);
  const currentWeekday = todayWeekday();

  return (
    <header className="restaurant-hero" style={{ backgroundColor: bgColor }}>
      {/* Mounted for every restaurant-subdomain route (App.js renders this
          outside <Routes>), so this one tag covers the menu, product
          detail, cart and order-success tabs alike — not just the menu
          list. Falls back to plain "Tablify" (Seo.js) until the
          restaurant's name has loaded. */}
      <Seo title={restaurant.name} />

      <div className="restaurant-hero-banner">
        {/* Clips only the photo/fallback/scrim to the banner's rounded
            corners — the language switcher below sits outside this
            wrapper so its dropdown menu isn't clipped by it too (was
            cut off on mobile, where the banner is short). */}
        <div className="restaurant-hero-banner-media">
          {restaurant.banner_image ? (
            <img
              className="restaurant-hero-banner-img"
              src={restaurant.banner_image}
              alt=""
            />
          ) : (
            <div
              className="restaurant-hero-banner-fallback"
              style={{ backgroundColor: bgColor }}
            />
          )}
          <div className="restaurant-hero-scrim" />
        </div>

        <div className="home-container restaurant-hero-utility">
          {restaurant.subscription_tier === "pro" &&
            restaurant.online_reservations_enabled !== false && (
            <Link to="/reservation" className="restaurant-hero-reservation-link">
              {t("reservationsNavLink", language)}
            </Link>
          )}
          <LanguageSwitcher />
        </div>
      </div>

      <div className="restaurant-hero-identity">
        <div className="home-container restaurant-hero-identity-inner">
          {hasLogo && (
            <span className="restaurant-hero-logo-card">
              <img
                className="restaurant-hero-logo"
                src={restaurant.logo_image}
                alt={restaurant.name || "Étterem logó"}
                onError={() => setLogoFailed(true)}
              />
            </span>
          )}

          <div className="restaurant-hero-text">
            {showName && (
              <h1 className="restaurant-hero-name" style={{ color: textColor }}>
                {restaurant.name}
              </h1>
            )}

            {!isCompact && restaurant.description && (
              <p
                className="restaurant-hero-description"
                style={{ color: textColor }}
              >
                {restaurant.description}
              </p>
            )}

            {!isCompact && hasStatus && (
              <details className="restaurant-hero-hours">
                <summary
                  className={`restaurant-hero-status ${
                    isOpenNow ? "is-open" : "is-closed"
                  }`}
                >
                  <span className="restaurant-hero-status-dot" />
                  {isOpenNow ? t("openNow", language) : t("closedNow", language)}
                  <FaChevronDown className="restaurant-hero-status-chevron" />
                </summary>

                <div className="restaurant-hero-hours-panel">
                  <div className="restaurant-hero-hours-title">
                    {t("openingHoursTitle", language)}
                  </div>

                  {openingHours.map((day) => (
                    <div
                      key={day.weekday}
                      className={`restaurant-hero-hours-row ${
                        day.weekday === currentWeekday ? "is-today" : ""
                      }`}
                    >
                      <span>{weekdayLabels[day.weekday]}</span>
                      <span>
                        {day.is_closed
                          ? t("closedNow", language)
                          : `${day.open_time}–${day.close_time}`}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default RestaurantHero;
